/* ============================================================================
   IMAGE PIPELINE — browser only
   ----------------------------------------------------------------------------
   Resize and compress happen on the requester's machine before anything leaves
   it. This module touches the DOM, so it lives outside the compiler, which
   stays pure and runs in Node and CI.

   In production the resulting blob goes to the Worker, which posts it to
     POST /api/REST/1.0/assets/image/content   (multipart/form-data)
   Eloqua returns fullImageUrl like
     /EloquaImages/clients/HOUSTONNFLHOLDINGSLP/{guid}_name.png
   The Worker prefixes https://images.ht.houstontexans.com and percent-encodes
   the braces. Verified against production seeds. Credentials never reach the
   browser.
   ========================================================================== */

import { SPECS, IMAGE_BUDGET } from "../compiler/index.js";

export { SPECS, IMAGE_BUDGET };

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("That file couldn't be read as an image."));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("That file couldn't be read."));
    reader.readAsDataURL(file);
  });

export async function processImage(file, specKey) {
  const spec = SPECS[specKey] || SPECS.hero;

  if (!/^image\/(jpeg|png|gif|webp)$/.test(file.type))
    throw new Error("Use a JPG or PNG.");

  if (file.type === "image/gif")
    throw new Error(
      "Animated GIFs can't be resized here — the animation would be lost. Note it for the email team and they'll handle it."
    );

  const img = await loadImage(file);
  const scale = Math.min(1, spec.w / img.naturalWidth);
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (spec.fmt === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(img, 0, 0, width, height);

  const bytesOf = (dataUrl) =>
    Math.round((dataUrl.length - dataUrl.indexOf(",") - 1) * 0.75);

  let quality = 0.88;
  let out = canvas.toDataURL(spec.fmt, quality);
  if (spec.fmt === "image/jpeg") {
    let guard = 0;
    while (bytesOf(out) > spec.bytes && quality > 0.4 && guard++ < 8) {
      quality -= 0.08;
      out = canvas.toDataURL(spec.fmt, quality);
    }
  }

  return {
    dataUrl: out,
    width,
    height,
    bytes: bytesOf(out),
    originalBytes: file.size,
    originalWidth: img.naturalWidth,
    spec,
    filename: file.name,
  };
}

export const kb = (b) =>
  b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
